import { Link } from '@tanstack/react-router'
import { useT } from '../../i18n'
import { demoGuideI18n } from '../../i18n/admin/demo-guide'

type ScenarioId =
  | 'meta_reset'
  | 'iq1_aybars_approve'
  | 'iq1_cross_side'
  | 'iq2_aybars_exit'
  | 'iq7_assortment'
  | 'wo_mehmet'
  | 'feedback_sarah'

type Step = {
  // Step text + link label are sourced from the i18n dict (per-scenario
  // arrays index-matched against this skeleton). Only routing data
  // (to/params/search) and role/persona hints live in code — narrative
  // copy is locale-driven so EN/TR switch in real time.
  link?: { to: string; params?: Record<string, string>; search?: Record<string, unknown> }
  roleHint?: 'admin' | 'seller' | 'supply'
  personaHint?: string
}

type Scenario = {
  id: ScenarioId
  category: 'IQ-1' | 'IQ-2' | 'IQ-7' | 'WO' | 'Feedback' | 'Meta'
  steps: Step[]
}

// Scenario skeleton: routing meta + role/persona hints only. Step text +
// link labels + scenario title + narrative live in i18n/admin/demo-guide.ts
// (parallel arrays index-matched against `steps`).
const SCENARIOS: Scenario[] = [
  {
    id: 'meta_reset',
    category: 'Meta',
    steps: [{ link: { to: '/admin' }, roleHint: 'admin' }],
  },
  {
    id: 'iq1_aybars_approve',
    category: 'IQ-1',
    steps: [
      { link: { to: '/admin/pending-updates' }, roleHint: 'admin' },
      {
        link: {
          to: '/admin/pending-updates/$id',
          params: { id: 'vus_a1b2c3' },
        },
      },
      {},
      { link: { to: '/admin/pending-updates' } },
    ],
  },
  {
    id: 'iq1_cross_side',
    category: 'IQ-1',
    steps: [
      { roleHint: 'supply', personaHint: 'so3_aybars' },
      { link: { to: '/supply/my-updates/new' } },
      { link: { to: '/supply/my-updates' } },
      { link: { to: '/admin/pending-updates' }, roleHint: 'admin' },
      {},
    ],
  },
  {
    id: 'iq2_aybars_exit',
    category: 'IQ-2',
    steps: [
      { link: { to: '/admin/vendor-replacements' }, roleHint: 'admin' },
      {
        link: {
          to: '/admin/vendor-replacements/$id',
          params: { id: 'vr_001' },
        },
      },
      {},
      {},
      {},
      {},
    ],
  },
  {
    id: 'iq7_assortment',
    category: 'IQ-7',
    steps: [
      { link: { to: '/admin/assortment' }, roleHint: 'admin' },
      {},
      {},
      {},
    ],
  },
  {
    id: 'wo_mehmet',
    category: 'WO',
    steps: [
      { roleHint: 'supply', personaHint: 'so2_mehmet' },
      { link: { to: '/supply/work-orders' } },
      {
        link: {
          to: '/supply/work-orders/$id',
          params: { id: 'wo_2026_0145' },
        },
      },
      {},
      { link: { to: '/admin' }, roleHint: 'admin' },
    ],
  },
  {
    id: 'feedback_sarah',
    category: 'Feedback',
    steps: [
      { roleHint: 'seller' },
      { link: { to: '/seller/my-products' } },
      {},
      { link: { to: '/seller/my-reports' } },
      { link: { to: '/admin/catalog-issues' }, roleHint: 'admin' },
      {},
      {},
    ],
  },
]

const CATEGORY_COLOR: Record<Scenario['category'], string> = {
  'IQ-1': 'bg-sky-100 text-sky-800',
  'IQ-2': 'bg-amber-100 text-amber-800',
  'IQ-7': 'bg-emerald-100 text-emerald-800',
  WO: 'bg-violet-100 text-violet-800',
  Feedback: 'bg-cyan-100 text-cyan-800',
  Meta: 'bg-slate-200 appearance-dark:bg-[var(--color-surface-muted)] text-slate-800 appearance-dark:text-[var(--color-text)]',
}

const ROLE_HINT_COLOR: Record<NonNullable<Step['roleHint']>, string> = {
  admin: 'bg-slate-900 text-white',
  seller: 'bg-orange-600 text-white',
  supply: 'bg-violet-700 text-white',
}

export function DemoGuide() {
  const t = useT(demoGuideI18n)
  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{t.page.title}</h1>
      <p className="mt-2 text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{t.page.description}</p>
      <div className="mt-3 rounded-lg border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] px-4 py-3 text-xs text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
        {t.page.helpBox}
      </div>

      <div className="mt-6 space-y-6">
        {SCENARIOS.map((s, idx) => {
          const copy = t.scenarios[s.id]
          return (
            <article
              key={s.id}
              className="rounded-lg border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] p-5"
            >
              <header className="flex items-start gap-3">
                <span className="text-xs font-medium text-slate-400 appearance-dark:text-[var(--color-text-muted)] tabular-nums pt-1">
                  {String(idx).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <h2 className="text-lg font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">
                      {copy.title}
                    </h2>
                    <span
                      className={
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ' +
                        CATEGORY_COLOR[s.category]
                      }
                    >
                      {s.category}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600 appearance-dark:text-[var(--color-text-muted)]">{copy.narrative}</p>
                </div>
              </header>

              <ol className="mt-4 space-y-2 text-sm">
                {s.steps.map((step, i) => {
                  const stepText = copy.steps[i] ?? ''
                  const linkLabel = copy.linkLabels[i] ?? ''
                  return (
                    <li key={i} className="flex items-start gap-2">
                      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 appearance-dark:bg-[var(--color-surface-muted)] text-xs font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-slate-700 appearance-dark:text-[var(--color-text)]">{stepText}</span>
                        {step.roleHint && (
                          <span
                            className={
                              'ml-2 inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium ' +
                              ROLE_HINT_COLOR[step.roleHint]
                            }
                          >
                            {t.stepRoleLabel(step.roleHint)}
                          </span>
                        )}
                        {step.personaHint && (
                          <span className="ml-1 inline-flex rounded-md bg-slate-100 appearance-dark:bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-xs font-mono text-slate-700 appearance-dark:text-[var(--color-text)]">
                            {step.personaHint}
                          </span>
                        )}
                        {step.link && linkLabel && (
                          <div className="mt-1">
                            <Link
                              to={step.link.to as '/admin'}
                              params={(step.link.params ?? {}) as { id: string }}
                              search={(step.link.search ?? {}) as never}
                              className="inline-flex items-center gap-1 text-xs text-brand-700 appearance-dark:text-[var(--color-text)] hover:underline"
                            >
                              → {linkLabel}
                            </Link>
                          </div>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ol>
            </article>
          )
        })}
      </div>

      <div className="mt-8 rounded-lg border border-dashed border-slate-300 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] p-4 text-sm text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
        <h3 className="font-medium text-slate-900 appearance-dark:text-[var(--color-text)]">{t.footer.title}</h3>
        <p className="mt-1">
          {t.footer.body
            .split('planning/')
            .flatMap((part, idx, arr) =>
              idx === arr.length - 1
                ? [part]
                : [
                    part,
                    <span key={`pl-${idx}`} className="font-mono">
                      planning/
                    </span>,
                  ],
            )}
        </p>
      </div>
    </div>
  )
}
